// src/pages/partners/PartnerPriceUploadDetail.tsx
//
// PartnerPriceUploadDetail
// - Displays a bulk upload history in a standardized table UI.
// - Table UI: StandardTableToolbar + StandardTableFrame + StandardPagination.
// - "Only Failure" filter uses MUI Switch (prevents styling inconsistencies).
// - Summary section uses MUI TextField (read-only).
// - Row status:
//   - uploadStatus !== OK => warning (standard row status).
// - Formatting:
//   - Numbers use thousand separators.
//   - discountRate is stored as a ratio (e.g., 0.1234) and displayed as percent with 2 decimals.
// - Keeps existing behavior: returns null while upload history is loading.
// - Pending actions use MUI Buttons (Cancel / Upload).
//
// Notes for maintainers
// - This page assumes uploadId/partnerId are URL params.
// - Filtering resets paging to page 1 to avoid empty pages after narrowing results.

import type React from 'react';
import { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Decimal } from 'decimal.js';
import { useNavigate, useParams } from 'react-router-dom';
import { getBulkDetailStatusMeta } from '@constants/bulkLabels';

import useAsyncEffect from '@hooks/useAsyncEffect';

import type {
  PartnerProductPriceBulkUpload,
  PartnerProductPriceBulkUploadDetailResponse,
} from '../../models/partnerBulk';
import {
  PartnerProductPriceBulkUploadDetailStatus,
  PartnerProductPriceBulkUploadStatus,
} from '../../models/partnerBulk';

import { usePriceUploadHistory } from '../../queries/partners/usePriceUploadHistory';
import { usePriceUploadDetails } from '../../queries/partners/usePriceUploadDetails';
import { usePriceUploadDetailsCount } from '../../queries/partners/usePriceUploadDetailsCount';
import { confirmPriceUpload } from '../../apis/partnerBulk';

import { useConfirm } from '@components/common/confirm/confirm.context';
import { useSystemAlert } from '@components/common/systemAlert/systemAlert.context';

import {
  StandardPagination,
  StandardTableFrame,
  StandardTableToolbar,
  cellSx,
} from '@components/common/table';

const PAGE_SIZE = 50;

/* ------------------------------------------------------------------ */
/* Formatting helpers                                                   */
/* ------------------------------------------------------------------ */

const formatNumber = (n: unknown) => {
  const v = typeof n === 'number' ? n : Number(n);
  if (!Number.isFinite(v)) return '-';
  return v.toLocaleString();
};

const formatPct = (rate: unknown) => {
  const v = typeof rate === 'number' ? rate : Number(rate);
  if (!Number.isFinite(v)) return '-';
  // discountRate is stored as ratio (e.g., 0.1234). Display as percent.
  return `${new Decimal(v).mul(100).toFixed(2)}%`;
};

type PriceStatus = 'VISIBLE' | 'HIDDEN';

const PRICE_STATUS_LABELS: Record<PriceStatus, string> = {
  VISIBLE: 'Visible',
  HIDDEN: 'Hidden',
};

const formatPriceStatus = (raw: unknown) => {
  const v = String(raw ?? '') as PriceStatus;
  return PRICE_STATUS_LABELS[v] ?? (v || '-');
};

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export const PartnerPriceUploadDetail = (): React.ReactElement | null => {
  const { partnerId: partnerIdParam, uploadId: uploadIdParam } = useParams();
  const navigate = useNavigate();

  const confirm = useConfirm();
  const systemAlert = useSystemAlert();

  const partnerId = useMemo(() => Number(partnerIdParam), [partnerIdParam]);
  const uploadId = useMemo(() => Number(uploadIdParam), [uploadIdParam]);

  const [onlyFailed, setOnlyFailed] = useState(false);

  const [summary, setSummary] = useState<PartnerProductPriceBulkUpload>({
    partnerProductPriceBulkUploadId: 0,
    filename: '',
    fileUrl: null,
    isDeleted: false,
    partnerId: 0,
    successCount: 0,
    failedCount: 0,
    totalCount: 0,
    status: PartnerProductPriceBulkUploadStatus.PENDING,
    createdAt: 0,
    updatedAt: 0,
  });

  // Paging (1-based)
  const [page, setPage] = useState(1);
  const handleChangePage = useCallback((next: number) => setPage(next), []);

  /* ---------------- Queries ---------------- */

  const { data: upload, isLoading: isLoadingHistory } = usePriceUploadHistory(
    partnerId,
    uploadId,
  );

  const { data: uploadDetailsData, isLoading: isLoadingDetails } =
    usePriceUploadDetails({
      partnerId,
      uploadId,
      page,
      pageSize: PAGE_SIZE,
      params: { onlyFailed },
    });

  // NOTE: This hook is assumed to support (partnerId, uploadId, { onlyFailed }).
  const { data: totalCount, isLoading: isLoadingCount } =
    usePriceUploadDetailsCount(partnerId, uploadId, { onlyFailed });

  const isTableLoading = Boolean(isLoadingDetails || isLoadingCount);

  /* ---------------- Handlers ---------------- */

  /**
   * Toggle "Only Failure" filter.
   * UX rule: Reset to page 1 when the filter changes.
   */
  const handleToggleOnlyFailure = useCallback(() => {
    setOnlyFailed((prev) => !prev);
    setPage(1);
  }, []);

  const handleCancel = useCallback(() => navigate(-1), [navigate]);

  /**
   * Confirm the upload result and apply pricing rules to the partner.
   * This action is irreversible in normal operations.
   */
  const handleConfirmUpload = useCallback(async () => {
    if (
      !partnerId ||
      !uploadId ||
      !Number.isFinite(partnerId) ||
      !Number.isFinite(uploadId)
    ) {
      systemAlert.error('Invalid partnerId/uploadId.');
      return;
    }

    const ok = await confirm({
      priority: 'warning',
      title: 'Upload this pricing file?',
      description:
        'This will apply the uploaded pricing rules to the partner. Please confirm before proceeding.',
      confirmText: 'Upload',
      cancelText: 'Cancel',
      autoFocusConfirm: false,
    });

    if (!ok) return;

    try {
      const { message } = await confirmPriceUpload(partnerId, uploadId);

      if (message !== 'OK') {
        systemAlert.error(message || 'Upload failed.');
        return;
      }

      systemAlert.success('Upload confirmed.');
      navigate(`/partners/${partnerId}/prices`);
    } catch {
      systemAlert.error('Failed to confirm upload. Please try again.');
    }
  }, [confirm, navigate, partnerId, systemAlert, uploadId]);

  /* ---------------- Summary hydration ---------------- */

  useAsyncEffect(async () => {
    if (!uploadId || !upload) return;
    const loaded = upload.data?.[0];
    if (!loaded) return;
    setSummary(loaded);
  }, [upload, uploadId]);

  // Keep existing behavior: do not render until the history header is loaded.
  if (isLoadingHistory) return null;

  const rows: PartnerProductPriceBulkUploadDetailResponse[] = (uploadDetailsData?.data ??
    []) as PartnerProductPriceBulkUploadDetailResponse[];

  const total = totalCount ?? 0;

  // (# + ProductCode + PriceStatus + DiscountRate + UploadState)
  const columnsCount = 5;

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      {/* Summary */}
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mt: 2, mb: 1.5 }}>
        <Stack spacing={1.5}>
          <Stack
            direction="row"
            alignItems="baseline"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={1}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                Upload Detail
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Review results and confirm applying the uploaded pricing rules.
              </Typography>
            </Box>

            <FormControlLabel
              control={
                <Switch checked={onlyFailed} onChange={handleToggleOnlyFailure} />
              }
              label="Only Failure"
              sx={{ userSelect: 'none' }}
            />
          </Stack>

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            gap={1.25}
            flexWrap="wrap"
            alignItems="flex-start"
          >
            <TextField
              label="File"
              value={summary.filename || '-'}
              size="small"
              fullWidth
              InputProps={{
                readOnly: true,
                endAdornment: summary.fileUrl ? (
                  <Typography
                    component="a"
                    href={summary.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="body2"
                    color="primary"
                    sx={{ textDecoration: 'underline', whiteSpace: 'nowrap', ml: 1 }}
                  >
                    Download
                  </Typography>
                ) : null,
              }}
              sx={{ minWidth: 360, flex: 1 }}
            />
            <TextField
              label="Total"
              value={formatNumber(summary.totalCount)}
              size="small"
              InputProps={{ readOnly: true }}
              sx={{ width: 160 }}
            />
            <TextField
              label="OK"
              value={formatNumber(summary.successCount)}
              size="small"
              InputProps={{ readOnly: true }}
              sx={{ width: 160 }}
            />
            <TextField
              label="Fail"
              value={formatNumber(summary.failedCount)}
              size="small"
              InputProps={{ readOnly: true }}
              sx={{ width: 160 }}
            />
          </Stack>
        </Stack>
      </Paper>

      {/* Toolbar */}
      <StandardTableToolbar
        page={page}
        pageSize={PAGE_SIZE}
        totalCount={total}
        onChangePage={handleChangePage}
        showTotalCount
        showPageJump
      />

      {/* Table */}
      <Box mt={1}>
        <StandardTableFrame<PartnerProductPriceBulkUploadDetailResponse>
          containerSx={{ borderRadius: 2 }}
          minWidth={900}
          columnsCount={columnsCount}
          isLoading={isTableLoading}
          loadingVariant="skeleton"
          skeletonRows={8}
          emptyText="No upload details found."
          // uploadStatus !== OK => warning background
          getRowStatus={(row) =>
            row.uploadStatus !== PartnerProductPriceBulkUploadDetailStatus.OK
              ? 'warning'
              : 'normal'
          }
          head={
            <TableRow>
              <TableCell width={80} align="right">
                #
              </TableCell>
              <TableCell sx={{ minWidth: 180 }}>Product Code</TableCell>
              <TableCell sx={{ minWidth: 140 }}>Price Status</TableCell>
              <TableCell width={160} align="right">
                Discount Rate
              </TableCell>
              <TableCell width={180} align="center">
                State
              </TableCell>
            </TableRow>
          }
          rows={rows}
          getRowKey={(row) => String(row.partnerProductPriceBulkUploadDetailId)}
          renderRow={(row, index) => {
            const chip = getBulkDetailStatusMeta(row.uploadStatus);

            return (
              <>
                <TableCell align="right" sx={cellSx.number}>
                  {(page - 1) * PAGE_SIZE + index + 1}
                </TableCell>

                <TableCell sx={cellSx.nowrap}>{row.productCode || '-'}</TableCell>

                <TableCell sx={{ ...cellSx.nowrap, maxWidth: 260 }}>
                  <Typography variant="body2" noWrap title={row.priceStatus || ''}>
                    {formatPriceStatus(row.priceStatus)}
                  </Typography>
                </TableCell>

                <TableCell align="right" sx={cellSx.nowrap}>
                  {formatPct(row.discountRate)}
                </TableCell>

                <TableCell align="center" sx={cellSx.nowrap}>
                  <Chip
                    label={chip.label}
                    color={chip.chip.color}
                    size="small"
                    variant={chip.chip.variant}
                  />
                </TableCell>
              </>
            );
          }}
        />
      </Box>

      {/* Pagination */}
      <Box mt={2} display="flex" justifyContent="center">
        <StandardPagination
          page={page}
          pageSize={PAGE_SIZE}
          totalCount={total}
          onChangePage={handleChangePage}
          size="small"
          shape="rounded"
        />
      </Box>

      {/* Pending actions */}
      {summary.status === PartnerProductPriceBulkUploadStatus.PENDING && (
        <Box mt={3}>
          <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
            <Button variant="outlined" color="error" onClick={handleCancel}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleConfirmUpload}>
              Upload
            </Button>
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default PartnerPriceUploadDetail;
